




















import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import InvestorsMixins from "@/mixins/http/Investor";

import CustomChart, { CustomChartItem } from "@/components/CustomChart.vue";
import { SlotObject } from "@/components/TimeSlotPicker.vue";
import {
  BenchmarkGranularity,
  BenchmarkTaxonomy,
} from "@/models/interfaces/Benchmark";
import { Media, TimeInterval } from "@/models";
import ConfrontationBenchmarkHelper from "@/services/ConfrontationBenchmarkHelper";
import Chart, { ChartTooltipModel } from "chart.js";

const requestTag = "investor-comparator-chart";

export interface ComparatorChartFilter {
  date: {
    from: string;
    to: string;
  };
  granularity: string;
  media: {
    radio: boolean;
    tv: boolean;
  };
  group: number | null;
  timeSlots?: SlotObject[];
  grpTarget?: string;
  showBenchmark?: boolean;
  benchmarkTaxonomy?: BenchmarkTaxonomy;
  benchmarkGranularity?: BenchmarkGranularity;
}

@Component({ components: { CustomChart } })
export default class ComparatorChart extends Mixins(InvestorsMixins) {
  @Prop() readonly filter!: ComparatorChartFilter;

  @Prop({ default: "bar" }) readonly type?: string;
  @Prop({ default: false }) readonly stacked?: boolean;
  @Prop({ default: false }) readonly percentage?: boolean;
  @Prop({ default: "spots" }) readonly unitOfMeasure?: string;

  @Watch("filter", { deep: true })
  onFilterChange() {
    this.load();
  }

  @Watch("unitOfMeasure")
  onUnitChanged() {
    this.load();
  }

  get decimals() {
    return this.unitOfMeasure == "grp_over_spots";
  }

  loading = false;
  benchmarkItems: any[] = [];
  items: any[] = [];

  get hasBenchmark(): boolean {
    return !!this.filter.showBenchmark && this.type !== "pie";
  }

  get chartLabels(): string[] {
    const start = this.$moment(this.filter.date.from);
    const end = this.$moment(this.filter.date.to).add(1, "days");

    const labels = new Set();

    switch (this.filter.granularity) {
      case "day":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("D MMM");
          labels.add(label);
        }

        break;

      case "sweek":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.clone().startOf("week").format("D MMM");
          labels.add(label);
        }

        break;

      case "month":
        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("MMM YYYY");
          labels.add(label.charAt(0).toUpperCase() + label.slice(1));
        }

        break;
    }

    return [...labels] as string[];
  }

  get chartItems(): CustomChartItem[] {
    const items = this.items.map((item: any) => {
      return {
        label: item.name,
        // eslint-disable-next-line
        data: item.timeIntervals.map((interval: any) => {
          if (this.unitOfMeasure == "seconds") {
            if (this.filter.media.radio && this.filter.media.tv) {
              return interval.secondsCount;
            } else if (this.filter.media.radio && !this.filter.media.tv) {
              return interval.radioSecondsCount;
            } else {
              return interval.tvSecondsCount;
            }
          } else if (this.unitOfMeasure == "spots") {
            if (this.filter.media.radio && this.filter.media.tv) {
              return interval.detectionsCount;
            } else if (this.filter.media.radio && !this.filter.media.tv) {
              return interval.radioDetectionsCount;
            } else {
              return interval.tvDetectionsCount;
            }
          } else if (this.unitOfMeasure == "grp") {
            if (this.filter.media.tv) {
              return interval.grpCount || 0;
            }
          } else if (this.unitOfMeasure == "grp_over_spots") {
            if (this.filter.media.tv) {
              return (
                (
                  Number.parseFloat(interval.grpCount) /
                  interval.tvDetectionsCount
                ).toFixed(2) || 0
              );
            }
          } else if (this.unitOfMeasure == "audience") {
            if (this.filter.media.tv) {
              return interval.audienceCount || 0;
            }
          }
        }),
      };
    });

    return this.hasBenchmark
      ? [
          ...items,
          ...(this.benchmarkChartItem ? [this.benchmarkChartItem] : []),
        ]
      : items;
  }

  get benchmarkChartItem() {
    if (
      this.benchmarkItems.length === 0 ||
      this.benchmarkItems[0].timeIntervals.length === 0
    ) {
      return null;
    }

    const timeIntervals = Array.from(
      this.benchmarkItems[0].timeIntervals.map(
        (interval: any) => interval.timeInterval
      )
    );
    const tooltips = timeIntervals.map((_, index) => ({
      beforeLabel: ["Altri da Benchmark Settore:"],
      label: this.benchmarkItems
        .map((item) => ({
          name: item.name,
          value: this.getBenchmarkTooltipValueForTimeInterval(
            item.timeIntervals[index]
          ) as number,
        }))
        .filter((item) => item.value && !isNaN(item.value))
        .sort((a, b) => b.value - a.value)
        .reduce(
          (tooltipLines, item) => [
            ...tooltipLines,
            `${item.name}: ${item.value}`,
          ],
          [] as string[]
        ),
    }));

    return {
      label: "BENCHMARK",
      data: timeIntervals.map((_, index) => {
        return this.getBenchmarkValueAtIndex(index);
      }),
      color: this.type === "bar" ? "#ff337a" : "#ff337922",
      borderColor: "#ff337a",
      borderDash: [10, 5],
      pointBackgroundColor: "#ffffff",
      pointBorderColor: "#ff337a",
      extra: { tooltips },
    };
  }

  private getBenchmarkValueAtIndex(index: number) {
    if (this.unitOfMeasure == "seconds") {
      if (this.filter.media.radio && this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].secondsCount
        );
      } else if (this.filter.media.radio && !this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].radioSecondsCount
        );
      } else {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].tvSecondsCount
        );
      }
    } else if (this.unitOfMeasure == "spots") {
      if (this.filter.media.radio && this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].detectionsCount
        );
      } else if (this.filter.media.radio && !this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].radioDetectionsCount
        );
      } else {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].tvDetectionsCount
        );
      }
    } else if (this.unitOfMeasure == "grp") {
      if (this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => Number.parseFloat(item.timeIntervals[index].grpCount) || 0
        );
      }
    } else if (this.unitOfMeasure == "grp_over_spots") {
      if (this.filter.media.tv) {
        return (
          (
            ConfrontationBenchmarkHelper.sumBy(
              this.benchmarkItems,
              (item) =>
                Number.parseFloat(item.timeIntervals[index].grpCount) || 0
            ) /
            ConfrontationBenchmarkHelper.sumBy(
              this.benchmarkItems,
              (item) => item.timeIntervals[index].tvDetectionsCount
            )
          ).toFixed(2) || 0
        );
      }
    } else if (this.unitOfMeasure == "audience") {
      if (this.filter.media.tv) {
        return ConfrontationBenchmarkHelper.sumBy(
          this.benchmarkItems,
          (item) => item.timeIntervals[index].audienceCount || 0
        );
      }
    }
  }

  private getBenchmarkTooltipValueForTimeInterval(timeInterval: any) {
    if (this.unitOfMeasure == "seconds") {
      if (this.filter.media.radio && this.filter.media.tv) {
        return timeInterval.secondsCount;
      } else if (this.filter.media.radio && !this.filter.media.tv) {
        return timeInterval.radioSecondsCount;
      } else {
        return timeInterval.tvSecondsCount;
      }
    } else if (this.unitOfMeasure == "spots") {
      if (this.filter.media.radio && this.filter.media.tv) {
        return timeInterval.detectionsCount;
      } else if (this.filter.media.radio && !this.filter.media.tv) {
        return timeInterval.radioDetectionsCount;
      } else {
        return timeInterval.tvDetectionsCount;
      }
    } else if (this.unitOfMeasure == "grp") {
      if (this.filter.media.tv) {
        return Number.parseFloat(timeInterval.grpCount) || 0;
      }
    } else if (this.unitOfMeasure == "grp_over_spots") {
      if (this.filter.media.tv) {
        return (
          (
            (Number.parseFloat(timeInterval.grpCount) || 0) /
            timeInterval.tvDetectionsCount
          ).toFixed(2) || 0
        );
      }
    } else if (this.unitOfMeasure == "audience") {
      if (this.filter.media.tv) {
        return timeInterval.audienceCount || 0;
      }
    }
  }

  mounted() {
    this.load();
  }

  async load() {
    if (this.filter.group == null) {
      return;
    }

    this.loading = true;
    const [items, benchmarkItems] = await Promise.all([
      this.getComparisons(this.filter.group, this.getPostData(), requestTag),
      this.getBenchmarkItems(),
    ]);
    this.items = items;
    this.benchmarkItems = benchmarkItems;

    this.loading = false;
  }

  getPostData() {
    let grpTarget = undefined;
    if (this.filter.grpTarget) {
      grpTarget = { grpTarget: this.filter.grpTarget };
    }
    return {
      filter: {
        time_slots: this.filter.timeSlots,
        date_range: [this.filter.date.from, this.filter.date.to],
      },
      granularity: this.filter.granularity,
      ...grpTarget,
    };
  }

  onItemHidden(index: number) {
    if (this.items[index] !== undefined) {
      this.$emit("item-hidden", this.items[index]);
    }
  }

  onItemVisible(index: number) {
    if (this.items[index] !== undefined) {
      this.$emit("item-visible", this.items[index]);
    }
  }

  private async getBenchmarkItems() {
    if (!this.hasBenchmark) {
      return [];
    }

    const data = {
      filter: {
        time_slots: this.filter.timeSlots,
        date_range: [this.filter.date.from, this.filter.date.to],
        sector: this.filter.benchmarkTaxonomy?.sector
          ? Array.isArray(this.filter.benchmarkTaxonomy?.sector)
            ? this.filter.benchmarkTaxonomy?.sector
            : [this.filter.benchmarkTaxonomy.sector]
          : [],
        category: this.filter.benchmarkTaxonomy?.category
          ? Array.isArray(this.filter.benchmarkTaxonomy?.category)
            ? this.filter.benchmarkTaxonomy?.category
            : [this.filter.benchmarkTaxonomy.category]
          : [],
        class: this.filter.benchmarkTaxonomy?.class
          ? Array.isArray(this.filter.benchmarkTaxonomy?.class)
            ? this.filter.benchmarkTaxonomy?.class
            : [this.filter.benchmarkTaxonomy.class]
          : [],
      },
      benchmark_granularity: this.filter.benchmarkGranularity,
      granularity: this.filter.granularity,
      type: this.unitOfMeasure,
      ...(this.filter.grpTarget ? { grpTarget: this.filter.grpTarget } : {}),
    };

    return await this.getComparisonsBenchmark(
      this.filter.group!,
      data,
      requestTag
    );
  }

  renderTooltip(chart: HTMLCanvasElement) {
    return (tooltipModel: ChartTooltipModel) => {
      let tooltipEl = document.getElementById(
        "chartjs-tooltip"
      ) as HTMLDivElement;

      if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "chartjs-tooltip";
        tooltipEl.classList.add("chartjs-tooltip");
        tooltipEl.style.boxSizing = "border-box";
        document.body.appendChild(tooltipEl);
      }

      tooltipEl.innerHTML = this.getTooltipContent(tooltipModel);

      if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = "0";
        return;
      }

      this.handleTooltipCaret(tooltipEl, chart, tooltipModel);

      tooltipEl.style.opacity = "1";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
      tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
      tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
      tooltipEl.style.padding =
        tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.backgroundColor = "#252f50";
      tooltipEl.style.color = "#fff";
      tooltipEl.style.borderRadius = "4px";
    };
  }

  private handleTooltipCaret(
    tooltipEl: HTMLDivElement,
    chart: HTMLCanvasElement,
    tooltipModel: ChartTooltipModel
  ) {
    const position = chart.getBoundingClientRect();
    const { pageXOffset, pageYOffset } = window;
    const {
      caretSize,
      caretX,
      caretY,
      height,
      width,
      xAlign,
      yAlign,
      xPadding,
      yPadding,
    } = tooltipModel;

    tooltipEl.classList.remove(
      "above",
      "middle",
      "below",
      "left",
      "center",
      "right",
      "no-transform"
    );

    if (yAlign === "center") {
      tooltipEl.classList.add("middle");
      tooltipEl.style.top =
        position.top + pageYOffset + caretY - height / 2 + "px";

      switch (xAlign) {
        case "left":
          tooltipEl.classList.add("left");
          tooltipEl.style.left =
            position.left +
            pageXOffset +
            caretX +
            caretSize +
            xPadding / 2 +
            "px";
          break;
        case "right":
          tooltipEl.classList.add("right");
          tooltipEl.style.left =
            position.left +
            pageXOffset +
            caretX -
            width -
            caretSize -
            xPadding / 2 +
            "px";
          break;
        default:
          tooltipEl.classList.add("center");
          tooltipEl.style.left =
            position.left +
            pageXOffset +
            caretX -
            width / 2 -
            xPadding / 2 +
            "px";
          break;
      }

      return;
    }

    if (yAlign === "top") {
      tooltipEl.classList.add("below");
      tooltipEl.style.top =
        position.top + pageYOffset + caretY + caretSize + yPadding / 2 + "px";
    } else {
      tooltipEl.classList.add("above");
      tooltipEl.style.top =
        position.top + pageYOffset + caretY - caretSize - yPadding / 2 + "px";
    }

    switch (xAlign) {
      case "left":
        tooltipEl.classList.add("left");
        tooltipEl.style.left =
          position.left + pageXOffset + caretX - caretSize - xPadding + "px";
        break;
      case "right":
        tooltipEl.classList.add("right");
        tooltipEl.style.left =
          position.left + pageXOffset + caretX - width + caretSize / 2 + "px";
        break;
      default:
        tooltipEl.classList.add("center");
        tooltipEl.style.left =
          position.left + pageXOffset + caretX - width / 2 - xPadding + "px";
        break;
    }
  }

  private getTooltipContent(tooltipModel: ChartTooltipModel) {
    if (!tooltipModel.body || tooltipModel.body.length === 0) {
      return "";
    }

    const titleLines = tooltipModel.title || [];
    const body = tooltipModel.body[0];
    const bodyBeforeLabelLines = body.before;
    const bodyLabelLines = body.lines;

    const titleDom = titleLines.map((line) => `<div>${line}</div>`);
    const bodyBeforeLabelDom = bodyBeforeLabelLines.map(
      (line) => `
        <div class="my-1">${line}</div>
    `
    );
    const bodyLabelDom = bodyLabelLines.reduce((accumulator, line) => {
      const [lineLabel, lineValue] = line.split(":");
      return `${accumulator}<div class="text-truncate">${lineLabel}</div><div class="pl-2">${lineValue}</div>`;
    }, "");

    return `
      <div style="max-width: 400px">
        ${titleDom}
        ${bodyBeforeLabelDom}
        <div class="tooltip-body">${bodyLabelDom}</div>
      </div>
    `;
  }
}
